/**
 * Planung KS — KW-basierte Auflagen-/Zielmengen-Planung
 *
 * Bildet den Excel-Tab „Planung KS" ab: pro KW (Spalte) werden Auflagen-/
 * Adressmengen je Segment (Zeile) geplant. Zwei Bloecke:
 *   Block 1 „Auflage p. Kampagne"
 *   Block 2 „Adressen"
 *
 * Vollstaendig unabhaengig von der bestehenden „Vorschlag RV"-Einsatzplanung:
 * eigene Tabellen (ks_planung, ks_kw_config, ks_daten, ks_segment), eigene
 * Routen (/api/ingest/ks-planung/*). Die Segmentliste kommt vollstaendig aus
 * der DB (GET /segmente) — Header- und „Auflage ges."-Zeilen sind Struktur
 * und werden fest pro Block gerendert.
 *
 * „Auflage ges."-Zeilen und Monats-Zwischensummenspalten werden client-seitig
 * berechnet. Mengen werden inline editiert und via PUT upsert-gespeichert.
 */

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Table, Trash, Plus, CircleNotch, CaretUp, CaretDown, PencilSimple } from '@phosphor-icons/react';
import { getIngestUrl, authFetch } from '../config';
import SubPageHeader from '../components/SubPageHeader';

// ===== Typen =====

interface Planung {
  id: number;
  name: string;
  status: string;
  beschreibung: string | null;
}

interface KwConfig {
  id: number;
  planung_id: number;
  kw_nummer: number;
  jahr: number;
  kw_label: string | null;
  sort_order: number;
  visible: number;
}

// Segment (Tabelle ks_segment) — pro Planung, voll flexibel
interface Segment {
  id: number;
  planung_id: number;
  segment_key: string;
  block: 1 | 2;
  role: 'component' | 'detail';
  label: string;
  indent: 0 | 1;
  sort_order: number;
}

// Spaltentyp: entweder eine echte KW oder eine Monats-Zwischensummenspalte
type Column =
  | { type: 'kw'; kw: KwConfig }
  | { type: 'monthSum'; monthIndex: number; kwIds: number[] };

// Feste Struktur-Zeilen pro Block (KEINE Segmente in der DB)
const BLOCK_HEADER_LABELS: Record<1 | 2, string> = {
  1: 'Auflage p. Kampagne',
  2: 'Adressen',
};
const BLOCK_TOTAL_LABEL = 'Auflage ges.';

const MONTH_NAMES = [
  'Januar', 'Februar', 'März', 'April', 'Mai', 'Juni',
  'Juli', 'August', 'September', 'Oktober', 'November', 'Dezember',
];

// ISO-8601: Donnerstag der angegebenen KW (zur Monatsbestimmung)
function isoWeekToDate(jahr: number, kw: number): Date {
  const simple = new Date(Date.UTC(jahr, 0, 1 + (kw - 1) * 7));
  const dow = simple.getUTCDay(); // 0=So
  const thursday = new Date(simple);
  const diff = (dow <= 4 ? 1 : 8) - dow + 3; // auf Donnerstag der ISO-Woche
  thursday.setUTCDate(simple.getUTCDate() + diff);
  return thursday;
}

function dataKey(kwConfigId: number, segmentKey: string): string {
  return `${kwConfigId}:${segmentKey}`;
}

export default function KsPlanung() {
  const [planungen, setPlanungen] = useState<Planung[]>([]);
  const [selectedPlanungId, setSelectedPlanungId] = useState<number | null>(null);
  const [kwConfigs, setKwConfigs] = useState<KwConfig[]>([]);
  const [daten, setDaten] = useState<Map<string, number | null>>(new Map());
  // Alle Segmente der Planung (aus ks_segment, inkl. ehemals fester Basis-Segmente)
  const [segmente, setSegmente] = useState<Segment[]>([]);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Neue-Planung-Dialog
  const [showNewDialog, setShowNewDialog] = useState(false);
  const [newName, setNewName] = useState('');
  const [newBeschreibung, setNewBeschreibung] = useState('');
  const [creating, setCreating] = useState(false);

  // KW-Schnellanlage
  const [newKwNummer, setNewKwNummer] = useState('');
  const [newKwJahr, setNewKwJahr] = useState(String(new Date().getFullYear()));
  const [addingKw, setAddingKw] = useState(false);

  // Inline-Edit
  const [editCell, setEditCell] = useState<{ kwId: number; segKey: string } | null>(null);
  const [editValue, setEditValue] = useState('');
  const [savingCell, setSavingCell] = useState<string | null>(null);

  // Custom-Segment-Verwaltung
  const [showSegDialog, setShowSegDialog] = useState(false);
  const [segLabel, setSegLabel] = useState('');
  const [segBlock, setSegBlock] = useState<1 | 2>(1);
  const [segRole, setSegRole] = useState<'component' | 'detail'>('detail');
  const [segIndent, setSegIndent] = useState<0 | 1>(0);
  const [savingSeg, setSavingSeg] = useState(false);
  const [editSegmentsMode, setEditSegmentsMode] = useState(false);

  // ===== Laden =====

  const loadPlanungen = useCallback(async () => {
    try {
      const res = await authFetch(getIngestUrl('/ks-planung/planungen'));
      const json = await res.json();
      const list: Planung[] = json.data || [];
      setPlanungen(list);
      if (list.length > 0 && selectedPlanungId == null) {
        setSelectedPlanungId(list[0].id);
      }
    } catch (e) {
      setError('Planungen konnten nicht geladen werden');
    }
  }, [selectedPlanungId]);

  const loadKwAndDaten = useCallback(async (planungId: number) => {
    setLoading(true);
    setError(null);
    try {
      const [kwRes, datenRes, segRes] = await Promise.all([
        authFetch(getIngestUrl(`/ks-planung/planungen/${planungId}/kw`)),
        authFetch(getIngestUrl(`/ks-planung/planungen/${planungId}/daten`)),
        authFetch(getIngestUrl(`/ks-planung/planungen/${planungId}/segmente`)),
      ]);
      const kwJson = await kwRes.json();
      const datenJson = await datenRes.json();
      const segJson = await segRes.json();

      const kws: KwConfig[] = (kwJson.data || []).filter((k: KwConfig) => k.visible);
      setKwConfigs(kws);

      const map = new Map<string, number | null>();
      for (const row of (datenJson.data || []) as Array<{ kw_config_id: number; segment_key: string; menge: number | null }>) {
        map.set(dataKey(row.kw_config_id, row.segment_key), row.menge);
      }
      setDaten(map);

      setSegmente((segJson.data || []) as Segment[]);
    } catch (e) {
      setError('KW-Daten konnten nicht geladen werden');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadPlanungen();
  }, [loadPlanungen]);

  useEffect(() => {
    if (selectedPlanungId != null) {
      loadKwAndDaten(selectedPlanungId);
    } else {
      setKwConfigs([]);
      setDaten(new Map());
      setSegmente([]);
    }
  }, [selectedPlanungId, loadKwAndDaten]);

  // ===== Aktionen =====

  const createPlanung = async () => {
    if (!newName.trim()) return;
    setCreating(true);
    setError(null);
    try {
      const res = await authFetch(getIngestUrl('/ks-planung/planungen'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: newName.trim(), beschreibung: newBeschreibung.trim() || null }),
      });
      const json = await res.json();
      if (!res.ok) {
        setError(json.error || 'Anlegen fehlgeschlagen');
        return;
      }
      const newId: number | undefined = json.data?.id;
      setShowNewDialog(false);
      setNewName('');
      setNewBeschreibung('');
      await loadPlanungen();
      if (newId) setSelectedPlanungId(newId);
    } catch (e) {
      setError('Anlegen fehlgeschlagen');
    } finally {
      setCreating(false);
    }
  };

  const addKw = async () => {
    if (selectedPlanungId == null) return;
    const kwNummer = parseInt(newKwNummer, 10);
    const jahr = parseInt(newKwJahr, 10);
    if (!kwNummer || kwNummer < 1 || kwNummer > 53) {
      setError('Ungültige KW (1-53)');
      return;
    }
    setAddingKw(true);
    setError(null);
    try {
      const res = await authFetch(getIngestUrl(`/ks-planung/planungen/${selectedPlanungId}/kw`), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ kw_nummer: kwNummer, jahr }),
      });
      if (res.status === 409) {
        setError(`KW${kwNummer}/${jahr} existiert bereits in dieser Planung`);
        return;
      }
      const json = await res.json();
      if (!res.ok) {
        setError(json.error || 'KW hinzufügen fehlgeschlagen');
        return;
      }
      setNewKwNummer('');
      await loadKwAndDaten(selectedPlanungId);
    } catch (e) {
      setError('KW hinzufügen fehlgeschlagen');
    } finally {
      setAddingKw(false);
    }
  };

  const deleteKw = async (kwId: number) => {
    if (selectedPlanungId == null) return;
    if (!window.confirm('KW wirklich löschen? Alle Mengen dieser KW gehen verloren.')) return;
    setError(null);
    try {
      const res = await authFetch(getIngestUrl(`/ks-planung/kw/${kwId}`), { method: 'DELETE' });
      if (!res.ok) {
        setError('KW löschen fehlgeschlagen');
        return;
      }
      await loadKwAndDaten(selectedPlanungId);
    } catch (e) {
      setError('KW löschen fehlgeschlagen');
    }
  };

  const clearPlanung = async () => {
    if (selectedPlanungId == null) return;
    if (!window.confirm('Planung leeren? Alle KWs und Mengen werden gelöscht (Header bleibt).')) return;
    setError(null);
    try {
      const res = await authFetch(getIngestUrl(`/ks-planung/planungen/${selectedPlanungId}/clear`), { method: 'DELETE' });
      if (!res.ok) {
        setError('Leeren fehlgeschlagen');
        return;
      }
      await loadKwAndDaten(selectedPlanungId);
    } catch (e) {
      setError('Leeren fehlgeschlagen');
    }
  };

  // ===== Custom-Segment-Aktionen =====

  const createSegment = async () => {
    if (selectedPlanungId == null) return;
    const label = segLabel.trim();
    if (!label) return;
    setSavingSeg(true);
    setError(null);
    try {
      const res = await authFetch(getIngestUrl(`/ks-planung/planungen/${selectedPlanungId}/segmente`), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ label, block: segBlock, role: segRole, indent: segIndent }),
      });
      if (!res.ok) {
        setError('Segment anlegen fehlgeschlagen');
        return;
      }
      setShowSegDialog(false);
      setSegLabel('');
      setSegBlock(1);
      setSegRole('detail');
      setSegIndent(0);
      await loadKwAndDaten(selectedPlanungId);
    } catch (e) {
      setError('Segment anlegen fehlgeschlagen');
    } finally {
      setSavingSeg(false);
    }
  };

  const renameSegment = async (id: number, label: string) => {
    if (selectedPlanungId == null) return;
    setError(null);
    try {
      const res = await authFetch(getIngestUrl(`/ks-planung/segmente/${id}`), {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ label }),
      });
      if (!res.ok) {
        setError('Umbenennen fehlgeschlagen');
        return;
      }
      await loadKwAndDaten(selectedPlanungId);
    } catch (e) {
      setError('Umbenennen fehlgeschlagen');
    }
  };

  const deleteSegment = async (id: number) => {
    if (selectedPlanungId == null) return;
    if (!window.confirm('Segment und erfasste Zahlen entfernen?')) return;
    setError(null);
    try {
      const res = await authFetch(getIngestUrl(`/ks-planung/segmente/${id}`), { method: 'DELETE' });
      if (!res.ok) {
        setError('Segment löschen fehlgeschlagen');
        return;
      }
      await loadKwAndDaten(selectedPlanungId);
    } catch (e) {
      setError('Segment löschen fehlgeschlagen');
    }
  };

  const moveSegment = async (id: number, dir: 'up' | 'down') => {
    if (selectedPlanungId == null) return;
    const cur = segmente.find((c) => c.id === id);
    if (!cur) return;
    // Segmente desselben Blocks nach sort_order (dann id) sortieren
    const siblings = segmente
      .filter((c) => c.block === cur.block)
      .sort((a, b) => (a.sort_order !== b.sort_order ? a.sort_order - b.sort_order : a.id - b.id));
    const idx = siblings.findIndex((c) => c.id === id);
    const neighborIdx = dir === 'up' ? idx - 1 : idx + 1;
    if (neighborIdx < 0 || neighborIdx >= siblings.length) return; // Rand -> no-op
    const neighbor = siblings[neighborIdx];
    setError(null);
    try {
      const r1 = await authFetch(getIngestUrl(`/ks-planung/segmente/${cur.id}`), {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ sort_order: neighbor.sort_order }),
      });
      const r2 = await authFetch(getIngestUrl(`/ks-planung/segmente/${neighbor.id}`), {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ sort_order: cur.sort_order }),
      });
      if (!r1.ok || !r2.ok) {
        setError('Verschieben fehlgeschlagen');
        return;
      }
      await loadKwAndDaten(selectedPlanungId);
    } catch (e) {
      setError('Verschieben fehlgeschlagen');
    }
  };

  const startEdit = (kwId: number, segKey: string) => {
    const v = daten.get(dataKey(kwId, segKey));
    setEditCell({ kwId, segKey });
    setEditValue(v == null ? '' : String(v));
  };

  const cancelEdit = () => {
    setEditCell(null);
    setEditValue('');
  };

  const commitEdit = async () => {
    if (!editCell || selectedPlanungId == null) {
      cancelEdit();
      return;
    }
    const { kwId, segKey } = editCell;
    const trimmed = editValue.trim();
    const menge: number | null = trimmed === '' ? null : parseInt(trimmed, 10);
    const cellId = dataKey(kwId, segKey);
    setSavingCell(cellId);
    setEditCell(null);
    try {
      const res = await authFetch(getIngestUrl('/ks-planung/daten'), {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          planung_id: selectedPlanungId,
          kw_config_id: kwId,
          segment_key: segKey,
          menge,
        }),
      });
      if (!res.ok) {
        setError('Speichern fehlgeschlagen');
        return;
      }
      // Lokaler Map-Update statt Voll-Reload
      setDaten((prev) => {
        const next = new Map(prev);
        next.set(cellId, Number.isNaN(menge as number) ? null : menge);
        return next;
      });
    } catch (e) {
      setError('Speichern fehlgeschlagen');
    } finally {
      setSavingCell(null);
      setEditValue('');
    }
  };

  // ===== Spalten-Layout (KWs nach Monat, danach Monats-Zwischensumme) =====

  const columns: Column[] = useMemo(() => {
    const sorted = [...kwConfigs].sort((a, b) =>
      a.jahr !== b.jahr ? a.jahr - b.jahr : a.kw_nummer - b.kw_nummer
    );

    const result: Column[] = [];
    let currentMonth: number | null = null;
    let currentKwIds: number[] = [];

    const flushMonth = () => {
      if (currentMonth != null && currentKwIds.length > 0) {
        result.push({ type: 'monthSum', monthIndex: currentMonth, kwIds: [...currentKwIds] });
      }
      currentKwIds = [];
    };

    for (const kw of sorted) {
      const month = isoWeekToDate(kw.jahr, kw.kw_nummer).getUTCMonth();
      if (currentMonth == null) {
        currentMonth = month;
      } else if (month !== currentMonth) {
        flushMonth();
        currentMonth = month;
      }
      result.push({ type: 'kw', kw });
      currentKwIds.push(kw.id);
    }
    flushMonth();

    return result;
  }, [kwConfigs]);

  // ===== Berechnungen =====

  const getMenge = useCallback(
    (kwId: number, segKey: string): number | null => daten.get(dataKey(kwId, segKey)) ?? null,
    [daten]
  );

  // Segmente je Block, sortiert nach sort_order (dann id)
  const segmentsByBlock = useMemo(() => {
    const m = new Map<1 | 2, Segment[]>();
    m.set(1, []);
    m.set(2, []);
    for (const seg of segmente) {
      m.get(seg.block)?.push(seg);
    }
    for (const list of [m.get(1)!, m.get(2)!]) {
      list.sort((a, b) => (a.sort_order !== b.sort_order ? a.sort_order - b.sort_order : a.id - b.id));
    }
    return m;
  }, [segmente]);

  // component-Zeilen je Block (fuer "Auflage ges."); 'detail' zaehlt nicht
  const componentKeysByBlock = useMemo(() => {
    const m = new Map<1 | 2, string[]>();
    m.set(1, []);
    m.set(2, []);
    for (const seg of segmente) {
      if (seg.role === 'component') m.get(seg.block)!.push(seg.segment_key);
    }
    return m;
  }, [segmente]);

  // "Auflage ges." einer KW-Spalte = Summe aller component-Zeilen des Blocks
  const blockTotalValue = useCallback(
    (block: 1 | 2, kwId: number): number | null => {
      const keys = componentKeysByBlock.get(block)!;
      let sum = 0;
      let any = false;
      for (const k of keys) {
        const v = getMenge(kwId, k);
        if (v != null) { sum += v; any = true; }
      }
      return any ? sum : null;
    },
    [componentKeysByBlock, getMenge]
  );

  // Monats-Zwischensumme einer Segmentzeile = Summe der menge-Werte der KWs des Monats
  const monthSumValue = useCallback(
    (segmentKey: string, kwIds: number[]): number | null => {
      let sum = 0;
      let any = false;
      for (const kwId of kwIds) {
        const v = getMenge(kwId, segmentKey);
        if (v != null) { sum += v; any = true; }
      }
      return any ? sum : null;
    },
    [getMenge]
  );

  // Monats-Zwischensumme der "Auflage ges."-Zeile eines Blocks
  const monthTotalValue = useCallback(
    (block: 1 | 2, kwIds: number[]): number | null => {
      let sum = 0;
      let any = false;
      for (const kwId of kwIds) {
        const v = blockTotalValue(block, kwId);
        if (v != null) { sum += v; any = true; }
      }
      return any ? sum : null;
    },
    [blockTotalValue]
  );

  // ===== Styles =====

  const fmt = (n: number | null | undefined): string =>
    n == null ? '' : n.toLocaleString('de-DE');

  const cellStyle: React.CSSProperties = {
    padding: '4px 8px',
    border: '1px solid #e5e7eb',
    fontSize: '0.8rem',
    whiteSpace: 'nowrap',
    textAlign: 'right',
  };
  const labelCellStyle: React.CSSProperties = {
    ...cellStyle,
    textAlign: 'left',
    minWidth: '260px',
  };
  const headerCellStyle: React.CSSProperties = {
    ...cellStyle,
    backgroundColor: '#f3f4f6',
    fontWeight: 600,
    textAlign: 'center',
  };
  const monthColStyle: React.CSSProperties = {
    backgroundColor: '#f3f4f6',
    fontWeight: 600,
  };

  const stickyLeft = (z: number = 10): React.CSSProperties => ({
    position: 'sticky',
    left: 0,
    zIndex: z,
    backgroundColor: 'white',
  });
  const stickyTop = (z: number = 5): React.CSSProperties => ({
    position: 'sticky',
    top: 0,
    zIndex: z,
  });

  const selectedPlanung = planungen.find((p) => p.id === selectedPlanungId) || null;

  // ===== Render =====

  return (
    <div style={{ minHeight: '100vh', backgroundColor: '#f9fafb' }}>
      <SubPageHeader title="Planung KS" icon={<Table size={24} weight="fill" />} />

      <div style={{ padding: '1.5rem' }}>
        {/* Toolbar */}
        <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '0.75rem', marginBottom: '1rem' }}>
          <label style={{ fontSize: '0.85rem', fontWeight: 600, color: '#374151' }}>Planung:</label>
          <select
            value={selectedPlanungId ?? ''}
            onChange={(e) => setSelectedPlanungId(e.target.value ? parseInt(e.target.value, 10) : null)}
            style={{ padding: '6px 10px', fontSize: '0.85rem', border: '1px solid #d1d5db', borderRadius: '4px' }}
          >
            {planungen.length === 0 && <option value="">— keine Planung —</option>}
            {planungen.map((p) => (
              <option key={p.id} value={p.id}>{p.name}</option>
            ))}
          </select>

          <button
            onClick={() => setShowNewDialog(true)}
            style={{
              display: 'flex', alignItems: 'center', gap: '0.4rem',
              padding: '6px 12px', fontSize: '0.85rem', cursor: 'pointer',
              backgroundColor: '#1e40af', color: 'white', border: 'none', borderRadius: '4px',
            }}
          >
            <Plus size={16} weight="bold" /> Neue Planung
          </button>

          {selectedPlanungId != null && (
            <button
              onClick={() => setShowSegDialog(true)}
              style={{
                display: 'flex', alignItems: 'center', gap: '0.4rem',
                padding: '6px 12px', fontSize: '0.85rem', cursor: 'pointer',
                backgroundColor: '#fff', color: '#1e40af', border: '1px solid #93c5fd', borderRadius: '4px',
              }}
            >
              <Plus size={16} weight="bold" /> Segment
            </button>
          )}

          {selectedPlanungId != null && (
            <button
              onClick={() => setEditSegmentsMode((v) => !v)}
              style={{
                display: 'flex', alignItems: 'center', gap: '0.4rem',
                padding: '6px 12px', fontSize: '0.85rem', cursor: 'pointer',
                backgroundColor: editSegmentsMode ? '#1e40af' : '#fff',
                color: editSegmentsMode ? 'white' : '#374151',
                border: '1px solid #d1d5db', borderRadius: '4px',
              }}
            >
              <PencilSimple size={16} /> {editSegmentsMode ? 'Segmente fertig' : 'Segmente bearbeiten'}
            </button>
          )}

          {selectedPlanungId != null && (
            <button
              onClick={clearPlanung}
              style={{
                display: 'flex', alignItems: 'center', gap: '0.4rem',
                padding: '6px 12px', fontSize: '0.85rem', cursor: 'pointer',
                backgroundColor: '#fff', color: '#b91c1c', border: '1px solid #fca5a5', borderRadius: '4px',
              }}
            >
              <Trash size={16} /> Planung leeren
            </button>
          )}
        </div>

        {/* KW-Schnellanlage */}
        {selectedPlanungId != null && (
          <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '0.5rem', marginBottom: '1rem' }}>
            <span style={{ fontSize: '0.85rem', color: '#374151' }}>KW hinzufügen:</span>
            <input
              type="number"
              min={1}
              max={53}
              placeholder="KW"
              value={newKwNummer}
              onChange={(e) => setNewKwNummer(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') addKw(); }}
              style={{ width: '70px', padding: '6px 8px', fontSize: '0.85rem', border: '1px solid #d1d5db', borderRadius: '4px' }}
            />
            <input
              type="number"
              min={2000}
              max={2100}
              placeholder="Jahr"
              value={newKwJahr}
              onChange={(e) => setNewKwJahr(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') addKw(); }}
              style={{ width: '90px', padding: '6px 8px', fontSize: '0.85rem', border: '1px solid #d1d5db', borderRadius: '4px' }}
            />
            <button
              onClick={addKw}
              disabled={addingKw}
              style={{
                display: 'flex', alignItems: 'center', gap: '0.4rem',
                padding: '6px 12px', fontSize: '0.85rem', cursor: addingKw ? 'default' : 'pointer',
                backgroundColor: '#059669', color: 'white', border: 'none', borderRadius: '4px', opacity: addingKw ? 0.6 : 1,
              }}
            >
              {addingKw ? <CircleNotch size={16} className="spin" /> : <Plus size={16} weight="bold" />} KW
            </button>
          </div>
        )}

        {error && (
          <div style={{ marginBottom: '1rem', padding: '0.75rem 1rem', backgroundColor: '#fee2e2', color: '#991b1b', border: '1px solid #fca5a5', borderRadius: '4px', fontSize: '0.85rem' }}>
            {error}
          </div>
        )}

        {loading && (
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', color: '#6b7280', fontSize: '0.9rem' }}>
            <CircleNotch size={18} className="spin" /> Lade …
          </div>
        )}

        {/* Leerer Zustand */}
        {!loading && planungen.length === 0 && (
          <div style={{ padding: '2rem', textAlign: 'center', color: '#6b7280', backgroundColor: 'white', border: '1px dashed #d1d5db', borderRadius: '8px' }}>
            Noch keine Planung vorhanden. Lege über „Neue Planung" eine an.
          </div>
        )}

        {!loading && selectedPlanungId != null && columns.length === 0 && (
          <div style={{ padding: '2rem', textAlign: 'center', color: '#6b7280', backgroundColor: 'white', border: '1px dashed #d1d5db', borderRadius: '8px' }}>
            {selectedPlanung ? `„${selectedPlanung.name}" hat noch keine KWs.` : 'Diese Planung hat noch keine KWs.'}
            {' '}Füge oben eine KW hinzu, um mit der Planung zu beginnen.
          </div>
        )}

        {/* Tabelle */}
        {!loading && selectedPlanungId != null && columns.length > 0 && (
          <div style={{ overflow: 'auto', maxHeight: 'calc(100vh - 240px)', border: '1px solid #e5e7eb', borderRadius: '6px', backgroundColor: 'white' }}>
            <table style={{ borderCollapse: 'collapse', minWidth: '100%' }}>
              <thead>
                <tr>
                  <th style={{ ...headerCellStyle, ...stickyLeft(20), ...stickyTop(20), textAlign: 'left', minWidth: '260px' }}>
                    Segment
                  </th>
                  {columns.map((col, i) => {
                    if (col.type === 'kw') {
                      return (
                        <th key={`kw-${col.kw.id}`} style={{ ...headerCellStyle, ...stickyTop(15), minWidth: '80px' }}>
                          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '2px' }}>
                            <span>{col.kw.kw_label || `KW${col.kw.kw_nummer}`}</span>
                            <span style={{ fontSize: '0.65rem', color: '#6b7280', fontWeight: 400 }}>{col.kw.jahr}</span>
                            <button
                              onClick={() => deleteKw(col.kw.id)}
                              title="KW löschen"
                              style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#9ca3af', padding: 0 }}
                            >
                              <Trash size={12} />
                            </button>
                          </div>
                        </th>
                      );
                    }
                    return (
                      <th key={`ms-${col.monthIndex}-${i}`} style={{ ...headerCellStyle, ...stickyTop(15), ...monthColStyle, minWidth: '90px' }}>
                        {MONTH_NAMES[col.monthIndex]}
                      </th>
                    );
                  })}
                </tr>
              </thead>
              <tbody>
                {([1, 2] as const).map((block) => {
                  const blockSegs = segmentsByBlock.get(block)!;
                  return (
                    <React.Fragment key={`block-${block}`}>
                      {/* Feste Header-Zeile des Blocks (Struktur, kein Segment) */}
                      <tr>
                        <td style={{ ...labelCellStyle, ...stickyLeft(10), backgroundColor: '#e5e7eb', fontWeight: 700 }}>
                          {BLOCK_HEADER_LABELS[block]}
                        </td>
                        {columns.map((col, i) => (
                          <td
                            key={`b${block}-header-${i}`}
                            style={{ ...cellStyle, backgroundColor: '#e5e7eb', ...(col.type === 'monthSum' ? monthColStyle : {}) }}
                          />
                        ))}
                      </tr>

                      {/* Segmente des Blocks (aus DB) */}
                      {blockSegs.map((seg) => (
                        <tr key={seg.segment_key}>
                          <td
                            style={{
                              ...labelCellStyle,
                              ...stickyLeft(10),
                              paddingLeft: seg.indent === 1 ? '24px' : '8px',
                              fontWeight: seg.role === 'component' ? 600 : 400,
                              backgroundColor: 'white',
                            }}
                          >
                            <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.4rem' }}>
                              <span>{seg.label}</span>
                              {editSegmentsMode && (
                                <span style={{ display: 'inline-flex', alignItems: 'center', gap: '2px', marginLeft: '0.25rem' }}>
                                  <button
                                    onClick={() => moveSegment(seg.id, 'up')}
                                    title="Nach oben"
                                    style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#9ca3af', padding: 0 }}
                                  >
                                    <CaretUp size={13} />
                                  </button>
                                  <button
                                    onClick={() => moveSegment(seg.id, 'down')}
                                    title="Nach unten"
                                    style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#9ca3af', padding: 0 }}
                                  >
                                    <CaretDown size={13} />
                                  </button>
                                  <button
                                    onClick={() => {
                                      const name = window.prompt('Neuer Name', seg.label);
                                      if (name != null && name.trim()) renameSegment(seg.id, name.trim());
                                    }}
                                    title="Umbenennen"
                                    style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#9ca3af', padding: 0 }}
                                  >
                                    <PencilSimple size={13} />
                                  </button>
                                  <button
                                    onClick={() => deleteSegment(seg.id)}
                                    title="Löschen"
                                    style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#b91c1c', padding: 0 }}
                                  >
                                    <Trash size={13} />
                                  </button>
                                </span>
                              )}
                            </span>
                          </td>

                          {columns.map((col, i) => {
                            // Monats-Zwischensummenspalte
                            if (col.type === 'monthSum') {
                              const v = monthSumValue(seg.segment_key, col.kwIds);
                              return (
                                <td key={`${seg.segment_key}-ms-${i}`} style={{ ...cellStyle, ...monthColStyle }}>
                                  {fmt(v)}
                                </td>
                              );
                            }

                            const kwId = col.kw.id;

                            // component/detail: editierbar
                            const cellId = dataKey(kwId, seg.segment_key);
                            const isEditing = editCell?.kwId === kwId && editCell?.segKey === seg.segment_key;
                            const isSaving = savingCell === cellId;

                            if (isEditing) {
                              return (
                                <td key={cellId} style={{ ...cellStyle, padding: 0 }}>
                                  <input
                                    autoFocus
                                    type="number"
                                    value={editValue}
                                    onChange={(e) => setEditValue(e.target.value)}
                                    onBlur={commitEdit}
                                    onKeyDown={(e) => {
                                      if (e.key === 'Enter') { e.preventDefault(); commitEdit(); }
                                      else if (e.key === 'Escape') { e.preventDefault(); cancelEdit(); }
                                    }}
                                    style={{ width: '100%', boxSizing: 'border-box', padding: '4px 6px', fontSize: '0.8rem', border: '2px solid #1e40af', textAlign: 'right' }}
                                  />
                                </td>
                              );
                            }

                            return (
                              <td
                                key={cellId}
                                onClick={() => !isSaving && startEdit(kwId, seg.segment_key)}
                                style={{ ...cellStyle, cursor: 'pointer', backgroundColor: isSaving ? '#eff6ff' : 'white' }}
                              >
                                {isSaving ? '…' : fmt(getMenge(kwId, seg.segment_key))}
                              </td>
                            );
                          })}
                        </tr>
                      ))}

                      {/* Feste "Auflage ges."-Summenzeile des Blocks (berechnet) */}
                      <tr>
                        <td style={{ ...labelCellStyle, ...stickyLeft(10), backgroundColor: '#f9fafb', fontWeight: 600 }}>
                          {BLOCK_TOTAL_LABEL}
                        </td>
                        {columns.map((col, i) => {
                          if (col.type === 'monthSum') {
                            return (
                              <td key={`b${block}-total-ms-${i}`} style={{ ...cellStyle, ...monthColStyle }}>
                                {fmt(monthTotalValue(block, col.kwIds))}
                              </td>
                            );
                          }
                          return (
                            <td key={`b${block}-total-${col.kw.id}`} style={{ ...cellStyle, backgroundColor: '#f9fafb', fontWeight: 600 }}>
                              {fmt(blockTotalValue(block, col.kw.id))}
                            </td>
                          );
                        })}
                      </tr>
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Neue-Planung-Dialog */}
      {showNewDialog && (
        <div
          style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}
          onClick={() => !creating && setShowNewDialog(false)}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{ backgroundColor: 'white', padding: '1.5rem', borderRadius: '8px', width: '420px', maxWidth: '90vw', boxShadow: '0 10px 30px rgba(0,0,0,0.2)' }}
          >
            <h2 style={{ margin: '0 0 1rem', fontSize: '1.15rem', color: '#1e40af' }}>Neue Planung KS</h2>
            <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 600, marginBottom: '0.25rem' }}>Name *</label>
            <input
              autoFocus
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') createPlanung(); }}
              style={{ width: '100%', boxSizing: 'border-box', padding: '8px 10px', fontSize: '0.9rem', border: '1px solid #d1d5db', borderRadius: '4px', marginBottom: '0.75rem' }}
            />
            <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 600, marginBottom: '0.25rem' }}>Beschreibung</label>
            <textarea
              value={newBeschreibung}
              onChange={(e) => setNewBeschreibung(e.target.value)}
              rows={3}
              style={{ width: '100%', boxSizing: 'border-box', padding: '8px 10px', fontSize: '0.9rem', border: '1px solid #d1d5db', borderRadius: '4px', marginBottom: '1rem', resize: 'vertical' }}
            />
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.5rem' }}>
              <button
                onClick={() => setShowNewDialog(false)}
                disabled={creating}
                style={{ padding: '8px 16px', fontSize: '0.85rem', cursor: 'pointer', backgroundColor: '#fff', color: '#374151', border: '1px solid #d1d5db', borderRadius: '4px' }}
              >
                Abbrechen
              </button>
              <button
                onClick={createPlanung}
                disabled={creating || !newName.trim()}
                style={{ padding: '8px 16px', fontSize: '0.85rem', cursor: creating || !newName.trim() ? 'default' : 'pointer', backgroundColor: '#1e40af', color: 'white', border: 'none', borderRadius: '4px', opacity: creating || !newName.trim() ? 0.6 : 1 }}
              >
                {creating ? 'Anlegen …' : 'Anlegen'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Segment-Anlage-Dialog */}
      {showSegDialog && (
        <div
          style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}
          onClick={() => !savingSeg && setShowSegDialog(false)}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{ backgroundColor: 'white', padding: '1.5rem', borderRadius: '8px', width: '420px', maxWidth: '90vw', boxShadow: '0 10px 30px rgba(0,0,0,0.2)' }}
          >
            <h2 style={{ margin: '0 0 1rem', fontSize: '1.15rem', color: '#1e40af' }}>Neues Segment</h2>

            <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 600, marginBottom: '0.25rem' }}>Label *</label>
            <input
              autoFocus
              value={segLabel}
              onChange={(e) => setSegLabel(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') createSegment(); }}
              style={{ width: '100%', boxSizing: 'border-box', padding: '8px 10px', fontSize: '0.9rem', border: '1px solid #d1d5db', borderRadius: '4px', marginBottom: '0.75rem' }}
            />

            <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 600, marginBottom: '0.25rem' }}>Block</label>
            <select
              value={segBlock}
              onChange={(e) => setSegBlock(parseInt(e.target.value, 10) as 1 | 2)}
              style={{ width: '100%', boxSizing: 'border-box', padding: '8px 10px', fontSize: '0.9rem', border: '1px solid #d1d5db', borderRadius: '4px', marginBottom: '0.75rem' }}
            >
              <option value={1}>Block 1 — Auflage p. Kampagne</option>
              <option value={2}>Block 2 — Adressen</option>
            </select>

            <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 600, marginBottom: '0.25rem' }}>Typ</label>
            <select
              value={segRole}
              onChange={(e) => setSegRole(e.target.value as 'component' | 'detail')}
              style={{ width: '100%', boxSizing: 'border-box', padding: '8px 10px', fontSize: '0.9rem', border: '1px solid #d1d5db', borderRadius: '4px', marginBottom: '0.75rem' }}
            >
              <option value="component">Komponente (zählt in Summe)</option>
              <option value="detail">Detail-Zeile (zählt nicht)</option>
            </select>

            <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 600, marginBottom: '0.25rem' }}>Einrückung</label>
            <select
              value={segIndent}
              onChange={(e) => setSegIndent(parseInt(e.target.value, 10) as 0 | 1)}
              style={{ width: '100%', boxSizing: 'border-box', padding: '8px 10px', fontSize: '0.9rem', border: '1px solid #d1d5db', borderRadius: '4px', marginBottom: '1rem' }}
            >
              <option value={0}>Hauptzeile</option>
              <option value={1}>Eingerückt</option>
            </select>

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.5rem' }}>
              <button
                onClick={() => setShowSegDialog(false)}
                disabled={savingSeg}
                style={{ padding: '8px 16px', fontSize: '0.85rem', cursor: 'pointer', backgroundColor: '#fff', color: '#374151', border: '1px solid #d1d5db', borderRadius: '4px' }}
              >
                Abbrechen
              </button>
              <button
                onClick={createSegment}
                disabled={savingSeg || !segLabel.trim()}
                style={{ padding: '8px 16px', fontSize: '0.85rem', cursor: savingSeg || !segLabel.trim() ? 'default' : 'pointer', backgroundColor: '#1e40af', color: 'white', border: 'none', borderRadius: '4px', opacity: savingSeg || !segLabel.trim() ? 0.6 : 1 }}
              >
                {savingSeg ? 'Anlegen …' : 'Anlegen'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
